import React, { useEffect, useRef, useState } from "react";
import Globe from "globe.gl";
import { MAP_CONFIG } from "./mapData";
import CityNotesPanel from "./CityNotesPanel";

// Convert Leaflet CRS.Simple pixel coords [y, x] to globe lat/lng.
// In Leaflet CRS.Simple: y=0 is bottom of image, y=height is top.
// Globe equirectangular: bottom = lat -90, top = lat +90.
const pixelToGlobe = ([y, x]) => ({
    lat: (y / MAP_CONFIG.height) * 180 - 90,
    lng: (x / MAP_CONFIG.width) * 360 - 180,
});

const CITY_COLORS = { metropolis: "#ffd700", city: "#ffffff", town: "#aaaaaa" };
const CITY_RADII = { metropolis: 0.5, city: 0.35, town: 0.2 };
const LABEL_SIZES = { metropolis: 1.3, city: 0.85 };

export default function GlobeMap() {
    const containerRef = useRef(null);
    const globeRef = useRef(null);
    const [cities, setCities] = useState([]);
    const [showCities, setShowCities] = useState(true);
    const [notesCity, setNotesCity] = useState(null);

    useEffect(() => {
        fetch("/api/cities.php")
            .then((r) => r.json())
            .then(setCities)
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!containerRef.current) return;

        const globe = Globe()(containerRef.current);
        globe
            .globeImageUrl("/erynos_clean_test.jpg")
            .backgroundColor("#050d18")
            .atmosphereColor("#2a4a7a")
            .atmosphereAltitude(0.12);

        const controls = globe.controls();
        controls.autoRotate = true;
        controls.autoRotateSpeed = 0.4;
        controls.enableDamping = true;
        controls.dampingFactor = 0.1;

        // Pause auto-rotate while user interacts, resume after 3 s
        let resumeTimer = null;
        const pauseRotation = () => {
            controls.autoRotate = false;
            clearTimeout(resumeTimer);
            resumeTimer = setTimeout(() => { controls.autoRotate = true; }, 3000);
        };
        containerRef.current.addEventListener("pointerdown", pauseRotation);

        globeRef.current = globe;

        return () => {
            clearTimeout(resumeTimer);
            if (containerRef.current) {
                containerRef.current.innerHTML = "";
            }
            globeRef.current = null;
        };
    }, []);

    useEffect(() => {
        const globe = globeRef.current;
        if (!globe) return;

        if (!showCities || cities.length === 0) {
            globe.pointsData([]).labelsData([]);
            return;
        }

        const geoData = cities.map((city) => ({
            ...city,
            ...pixelToGlobe(city.position),
        }));

        // Only show permanent labels for metropolis and city
        const labelData = geoData.filter((d) => d.size !== "town");

        globe
            .pointsData(geoData)
            .pointLat("lat")
            .pointLng("lng")
            .pointColor((d) => CITY_COLORS[d.size] || "#fff")
            .pointRadius((d) => CITY_RADII[d.size] || 0.2)
            .pointAltitude(0.01)
            .onPointClick((d) => setNotesCity(d))
            .labelsData(labelData)
            .labelLat("lat")
            .labelLng("lng")
            .labelText("name")
            .labelSize((d) => LABEL_SIZES[d.size] || 0.6)
            .labelColor(() => "rgba(255,255,255,0.9)")
            .labelResolution(2)
            .labelAltitude(0.015)
            .labelDotRadius(0);
    }, [cities, showCities]);

    return (
        <div style={{ width: "100%", height: "100%", position: "relative" }}>
            <div ref={containerRef} style={{ width: "100%", height: "100%" }} />

            <div
                style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    alignItems: "flex-end",
                }}
            >
                <button
                    onClick={() => setShowCities((v) => !v)}
                    style={{
                        padding: "8px 16px",
                        backgroundColor: showCities ? "#2ecc71" : "#4a5568",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontWeight: "bold",
                    }}
                >
                    Cities: {showCities ? "On" : "Off"}
                </button>
            </div>

            {notesCity && (
                <CityNotesPanel
                    city={notesCity}
                    onClose={() => setNotesCity(null)}
                    onEdit={null}
                />
            )}
        </div>
    );
}
